<template>
  <div class="seat-reservation-page">
    <LoadingOverlay
      v-if="isLoading"
      text="Behandler setevalg..."
    />
    <TravelPlannerHero
      :heading="$t('travelPlanner.seat.title')"
      :description="$t(
        myTravelStore.isInRebookingMode 
          ? 'travelPlanner.seat.rebookDescription' 
          : 'travelPlanner.seat.description'
      )"
    />
    <section class="grid-layout page-wrapper">
      <div
        class="grid-layout__main"
        v-if="isLoading"
      >
        Loading...
      </div>
      <div
        class="grid-layout__main seat-reservation-page__main"
        v-else-if="journey"
      >
        <DirectJourney
          v-if="!hasActiveTransfers"
          ref="directJourneyRef"
          :journey="journey"
          :hasInboundTrip="!!journey?.inbound_booking_tariff_segments?.length"
          :passengerInfo="journey.passengers"
          @seat-selected="handleSeatSelected"
        />
        <TransferJourney
          v-else
          ref="transferJourneyRef"
          :journey="journey"
          :hasInboundTrip="!!journey?.inbound_booking_tariff_segments?.length"
          :passengerInfo="journey.passengers"
          @seat-selected="handleSeatSelected"
        />
      </div>
      <section class="grid-layout__sidebar">
        <MyTravel
          v-if="!isMobileView"
          :origin="journeyStore.OriginStation.name"
          :destination="journeyStore.destinationStation.name"
          :has-return-travel="
            !!journey?.inbound_booking_tariff_segments?.length
          "
          :showTotalPrice="false"
        />
        <SeatSidebar :journey="journey" />
      </section>
    </section>
    <NavigationBar
      :price-tag="
        seatStore.totalSelectedSeatsPrice > 0
          ? {
              amount: seatStore.totalSelectedSeatsPrice,
              currency: 'NOK',
            }
          : undefined
      "
    >
      <div class="seat-reservation-page__nav-bar">
        <SelectedSeatsDisplay
          v-if="!hasActiveTransfers"
          :outboundSeats="outboundSelectedSeats"
          :inboundSeats="inboundSelectedSeats"
          :hasInboundTrip="!!journey?.inbound_booking_tariff_segments?.length"
        />
        <div class="navigation-bar__upper">
          <PriceTag 
            v-if="!myTravelStore.isInRebookingMode" 
            :isRebooking="myTravelStore.isInRebookingMode" 
          />
          <button
            class="navigation-bar__details-button button button--reset"
            @click="toggleDrawer"
            v-if="isMobileView"
          >
            {{ $t('travelPlanner.seat.showDetails') }}
            <svg class="trailing__chevron">
              <use :xlink:href="`#${chevron}`" />
            </svg>
          </button>
        </div>
        <div class="seat-reservation-page__nav-bar-right">
          <div class="seat-reservation-page__nav-bar-buttons">
            <button
              class="button button--medium button--secondary"
              @click="handleBack"
            >
              {{ $t('common.actions.back') }}
            </button>
            <button
              class="button button--medium button--primary"
              :class="['disabled', isLoading || !hasValidSeatSelection]"
              :disabled="isLoading || !hasValidSeatSelection"
              @click="handleContinueClick"
            >
              {{
                seatStore.hasSelectedSeats
                  ? $t('common.actions.continue')
                  : $t('travelPlanner.seat.skipSelection')
              }}
            </button>
          </div>
        </div>
      </div>
    </NavigationBar>
    <ConfirmationModal
      v-if="showPartialSeatModal"
      :is-open="showPartialSeatModal"
      :heading="$t('travelPlanner.seat.partialSeatModal.title')"
      :message="$t('travelPlanner.seat.partialSeatModal.description')"
      :confirm-text="$t('travelPlanner.seat.skipSeats')"
      :cancel-text="$t('travelPlanner.seat.selectSeats')"
      @confirm="handleConfirmPartialSeats"
      @cancel="showPartialSeatModal = false"
    />
    <BottomDrawer
      v-if="isMobileView"
      :is-open="isDrawerOpen"
      @close="toggleDrawer"
    >
      <template #header>
        <h2 class="title-medium">
          {{ $t('searchResult.navigation.drawerTitel') }}
        </h2>
      </template>
      <MyTravel
        :origin="journeyStore.OriginStation.name"
        :destination="journeyStore.destinationStation.name"
        :has-return-travel="!!journey?.inbound_booking_tariff_segments?.length"
        :showTotalPrice="false"
      />
      <template #footer>
        <button
          class="button button--medium button--secondary button--fullwidth"
          @click="toggleDrawer"
        >
          Lukk
        </button>
      </template>
    </BottomDrawer>
  </div>
</template>

<script lang="ts">
  import type { SelectedSeat } from '@/types/seatTypes'
  import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { storeToRefs } from 'pinia'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import {
    fetchBookingData,
    updateSeatSelection,
    searchProducts,
    submitProducts,
  } from '@/services/journeyService'
  import DirectJourney from '@/components/TravelPlanner/SeatReservation/DirectJourney.vue'
  import TransferJourney from '@/components/TravelPlanner/SeatReservation/TransferJourney.vue'
  import TravelPlannerHero from '@/components/TravelPlanner/TravelPlannerHero.vue'
  import MyTravel from '@/components/TravelPlanner/MyTravel/MyTravel.vue'
  import SeatSidebar from '@/components/TravelPlanner/SeatReservation/SeatSidebar.vue'
  import NavigationBar from '@/components/TravelPlanner/NavigationBar/NavigationBar.vue'
  import ConfirmationModal from '@/components/BaseComponents/Modal/ConfirmationModal.vue'
  import PriceTag from '@/components/TravelPlanner/PriceTag.vue'
  import SelectedSeatsDisplay from '@/components/TravelPlanner/SeatReservation/SelectedSeatsDisplay.vue'
  import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'
  import LoadingOverlay from '@/components/Common/LoadingOverlay.vue'
  import chevron from '@/assets/icons/chevron.svg'

  export default defineComponent({
    name: 'SeatReservationPage',

    components: {
      DirectJourney,
      TransferJourney,
      TravelPlannerHero,
      MyTravel,
      SeatSidebar,
      NavigationBar,
      ConfirmationModal,
      PriceTag,
      SelectedSeatsDisplay,
      BottomDrawer,
      LoadingOverlay,
    },

    setup() {
      const router = useRouter()
      const route = useRoute()
      const seatStore = useSeatReservationStore()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const myTravelStore = useMyTravelStore()
      const isLoading = ref(false)
      const showPartialSeatModal = ref(false)
      const currentBookingNumber = ref<string | null>(null)
      const seatPrices = ref<{ [key: string]: { [key: number]: number } }>({})
      const isDrawerOpen = ref(false)
      const isMobileView = ref(false)
      const transferJourneyRef = ref<any>(null)
      const directJourneyRef = ref<any>(null)

      const { currentJourney: journey } = storeToRefs(seatStore)

      const initializeSeatProducts = async (
        segment: any,
        direction: 'outbound' | 'inbound'
      ) => {
        try {
          const response = await searchProducts(
            segment.service_name,
            direction,
            segment.origin_station_code,
            segment.destination_station_code,
            segment.schedule_date,
            [{ code: 'SF' }] // Add this to specify we want seat fares
          )
          await seatStore.initializeSeatProducts(response, direction)
          return response
        } catch (error) {
          console.error('Error fetching seat products:', error)
          throw error
        }
      }

      const handleCarriageLayout = async (layout: any) => {
        try {
          seatStore.setSeatProperties(layout.seat_properties)

          const currentSegment = journey.value?.outbound_segments?.[0]
          if (currentSegment) {
            await initializeSeatProducts(currentSegment, 'outbound')
          }

          layout.carriages.forEach((carriage: any) => {
            carriage.seats = carriage.seats.map((seat: any) => {
              const propertyCode = seat.property_codes?.[0] || 'STANDARD_SEAT'
              const seatProduct = seatStore.getSeatProduct(propertyCode)

              return {
                ...seat,
                price: seatProduct?.price,
                description: seatProduct?.description,
                tariffCode: seatProduct?.tariffCode,
              }
            })
          })
        } catch (error) {
          console.error('Error handling carriage layout:', error)
        }
      }

      const handleContinue = async () => {
        if (isLoading.value) return

        isLoading.value = true
        try {
          if (!currentBookingNumber.value) {
            throw new Error('No booking number found')
          }

          // IMPORTANT: Delegate to the appropriate journey component's handleContinue method
          if (hasActiveTransfers.value) {
            // For TransferJourney, delegate to its handleContinue method
            
            if (transferJourneyRef.value) {
              try {
                await transferJourneyRef.value.handleContinue()
              } catch (error) {
                console.error('[ERROR] TransferJourney handleContinue failed:', error)
                // Fallback navigation if TransferJourney fails
                if (route.query.isRebooking) {
                  await router.push({
                    name: 'EquipmentPage',
                    query: {
                      isRebooking: route.query.isRebooking,
                      rebookingType: route.query.rebookingType,
                      bookingId: route.query.bookingId,
                      itemsToReplace: route.query.itemsToReplace
                    }
                  })
                } else {
                  await router.push('/reiseplanlegger/tillegg')
                }
              }
            } else {
              console.error('[ERROR] TransferJourney ref not found - cannot delegate')
              // Fallback: navigate without processing seats
              if (route.query.isRebooking) {
                await router.push({
                  name: 'EquipmentPage',
                  query: {
                    isRebooking: route.query.isRebooking,
                    rebookingType: route.query.rebookingType,
                    bookingId: route.query.bookingId,
                    itemsToReplace: route.query.itemsToReplace
                  }
                })
              } else {
                await router.push('/reiseplanlegger/tillegg')
              }
            }
            return
          } else {
            
            if (directJourneyRef.value) {
              try {
                await directJourneyRef.value.handleContinue()
                
                if (route.query.isRebooking) {
                  await router.push({
                    name: 'EquipmentPage',
                    query: {
                      isRebooking: route.query.isRebooking,
                      rebookingType: route.query.rebookingType,
                      bookingId: route.query.bookingId,
                      itemsToReplace: route.query.itemsToReplace
                    }
                  })
                } else {
                  await router.push('/reiseplanlegger/tillegg')
                }
                return
              } catch (error) {
                console.error('[ERROR] DirectJourney handleContinue failed:', error)
                // DO NOT fall through to legacy processing - show error and stay on page
                errorStore.setError('Kunne ikke oppdatere setevalg. Vennligst prøv igjen.')
                isLoading.value = false
                return
              }
            } else {
              console.error('[ERROR] DirectJourney ref not found - falling back to legacy processing')
              // Fall through to legacy processing
            }
          }

          const selectedSeats = seatStore.getSelectedSeatsForBooking()
          if (!selectedSeats.length) {
            if (route.query.isRebooking) {
              await router.push({
                name: 'EquipmentPage',
                query: {
                  isRebooking: route.query.isRebooking,
                  rebookingType: route.query.rebookingType,
                  bookingId: route.query.bookingId,
                  itemsToReplace: route.query.itemsToReplace
                }
              })
            } else {
              await router.push('/reiseplanlegger/tillegg')
            }
            return
          }
          const seatsByPassenger = selectedSeats.reduce((acc, seat) => {
            const key = `${seat.passenger_id}|${seat.direction}`
            if (!acc[key]) {
              acc[key] = []
            }
            acc[key].push(seat)
            return acc
          }, {} as Record<string, SelectedSeat[]>)

          let currentBooking;
          try {
            const bookingStore = useBookingStore();
            currentBooking = await bookingStore.fetchBookingData(currentBookingNumber.value);
          } catch (error) {
            console.error('[ERROR] Failed to fetch current booking:', error);
            errorStore.setError('Failed to update seat selection');
            isLoading.value = false;
            return;
          }

          const existingSeatProducts = (currentBooking?.additional_products || [])
            .filter(product => product.type === 'SF')
            .reduce((acc, product) => {
              acc[product.passenger_id] = product;
              return acc;
            }, {} as Record<string, any>);
            
          for (const [key, seats] of Object.entries(seatsByPassenger)) {
            const [passengerId, direction] = key.split('|')
            const seat = (seats as SelectedSeat[])[0]

            if (seat.tariffCode) {
              const existingProduct = existingSeatProducts[passengerId];
              
              const isRebooking = myTravelStore.isInRebookingMode || !!existingProduct;
              
              try {
                const response = await seatStore.patchBookingWithProduct(
                  currentBookingNumber.value,
                  seat.tariffCode,
                  direction as 'outbound' | 'inbound',
                  passengerId,
                  existingProduct?.id,
                  { 
                    carriage: seat.carriage || '',
                    number: seat.seat_number || ''
                  },
                  true,
                  seat.segmentId
                )

                if (response?.status === 204 || response?.success) {
                } else if (response?.data?.booking) {
                } else {
                  console.warn('[WARN] Unexpected response format:', response);
                }
              } catch (error) {
                console.error('[ERROR] Failed to patch product:', error)
                errorStore.setError('Failed to update seat selection')
                isLoading.value = false
                return
              }
            }
          }

          try {
            if (route.query.isRebooking) {
              await router.push({
                name: 'EquipmentPage',
                query: {
                  isRebooking: route.query.isRebooking,
                  rebookingType: route.query.rebookingType,
                  bookingId: route.query.bookingId,
                  itemsToReplace: route.query.itemsToReplace
                }
              })
            } else {
              await router.push('/reiseplanlegger/tillegg')
            }
          } catch (error) {
            console.error('[ERROR] Failed to patch seats:', error)
            errorStore.setError('Failed to update seat selection')
          }
        } catch (error) {
          console.error('[ERROR] Failed to process seat selection:', error)
          errorStore.setError('Failed to process seat selection')
        } finally {
          isLoading.value = false
        }
      }

      const loadJourneyData = async () => {
        try {
          const bookingNumber =
            journeyStore.bookingData?.booking_number ||
            bookingStore.bookingData?.booking_number
          if (!bookingNumber) {
            router.push('/reiseplanlegger/velg-reise')
            return
          }

          currentBookingNumber.value = bookingNumber
          const response = await fetchBookingData(bookingNumber)
          const bookingData = response.data.booking
          seatStore.initializeJourney(bookingData)
          bookingStore.setBookingData(bookingData)

          const outboundSeats = myTravelStore.outboundSelectedSeats
          const inboundSeats = myTravelStore.inboundSelectedSeats

          if (outboundSeats.length > 0 || inboundSeats.length > 0) {
            outboundSeats.forEach((seat) => {
              seatStore.selectSeat({
                ...seat,
                direction: 'outbound',
              })
            })

            inboundSeats.forEach((seat) => {
              seatStore.selectSeat({
                ...seat,
                direction: 'inbound',
              })
            })
          }

          isLoading.value = false
        } catch (error) {
          console.error('Failed to load journey data:', error)
          isLoading.value = false
        }
      }

      const getPriceKey = (
        segment: {
          service_identifier: string
          departure_station: { _u_i_c_station_code: string }
          arrival_station: { _u_i_c_station_code: string }
          travel_date: string
        },
        direction: 'outbound' | 'inbound'
      ): string => {
        return `${direction}_${segment.service_identifier}_${segment.departure_station._u_i_c_station_code}_${segment.arrival_station._u_i_c_station_code}_${segment.travel_date}`
      }

      const loadSeatPrices = async (
        legIndex: number,
        direction: 'outbound' | 'inbound'
      ): Promise<Record<number, number>> => {
        if (!journey.value) return {}

        const segment =
          direction === 'outbound'
            ? journey.value.outbound_segments[legIndex]
            : journey.value.inbound_segments[legIndex]

        if (!segment) return {}

        const key = getPriceKey(segment, direction)
        if (!key) return {}

        try {
          const prices = await searchProducts(
            segment.service_name,
            direction,
            segment.departure_station._u_i_c_station_code,
            segment.arrival_station._u_i_c_station_code,
            segment.travel_date,
            [{ code: 'SF' }]
          )

          await seatStore.initializeSeatProducts(prices, direction)

          if (!prices?.data?.segment?.products) {
            return {}
          }

          const product = prices.data.segment.products.find(
            (p: any) => p.product_code === 'SF'
          )

          if (product?.passenger_fares?.[0]?.price) {
            const price = product.passenger_fares[0].price.amount / 100
            seatPrices.value[key] = { [segment.sequence_number]: price }
            return { [segment.sequence_number]: price }
          }

          return {}
        } catch (error) {
          console.error('Error loading seat prices:', error)
          return {}
        }
      }

      const handleSeatSelected = async (
        seat: SelectedSeat | null,
        legIndex: number,
        direction: 'outbound' | 'inbound'
      ) => {
        if (!journey.value) return

        const segment =
          direction === 'outbound'
            ? journey.value.outbound_segments[legIndex]
            : journey.value.inbound_segments[legIndex]

        if (!segment) return

        let price = 0
        if (seat && seat.sequence_number !== undefined) {
          const prices: Record<number, number> =
            seatPrices.value[getPriceKey(segment, direction)] ||
            (await loadSeatPrices(legIndex, direction))

          price = prices[seat.sequence_number] || 0

          const myTravelStore = useMyTravelStore()
          const selectedSeats = seatStore.getSelectedSeatsForLeg(
            direction,
            legIndex
          )
          const totalPrice = selectedSeats.reduce(
            (total, s) => total + (s.price?.amount || 0),
            0
          )
          myTravelStore.updateSelectedSeats(
            direction,
            selectedSeats,
            totalPrice
          )
        }

        if (seat) {
          const seatData = {
            ...seat,
            direction,
            legIndex,
            price: { amount: price },
          }
          await seatStore.handleSeatSelection({
            seat: seatData,
            segment,
            direction,
            legIndex,
          })
        }
      }

      const handleContinueClick = () => {
        if (
          journey.value?.inbound_booking_tariff_segments?.length &&
          hasPartialSeatSelection.value
        ) {
          showPartialSeatModal.value = true
        } else {
          handleContinue()
        }
      }

      const hasPartialSeatSelection = computed(() => {
        if (!journey.value?.inbound_booking_tariff_segments?.length)
          return false

        const outboundSegment =
          journey.value.outbound_booking_tariff_segments[0]
        const inboundSegment = journey.value.inbound_booking_tariff_segments[0]

        const outboundHasSeats =
          outboundSegment?.required_products?.some((p: any) => 'seat' in p) ||
          false
        const inboundHasSeats =
          inboundSegment?.required_products?.some((p: any) => 'seat' in p) ||
          false

        const hasOutboundSeats =
          seatStore.getSelectedSeatsForLeg('outbound', 0).length > 0
        const hasInboundSeats =
          seatStore.getSelectedSeatsForLeg('inbound', 0).length > 0

        if (outboundHasSeats && inboundHasSeats) {
          return (
            (hasOutboundSeats && !hasInboundSeats) ||
            (!hasOutboundSeats && hasInboundSeats)
          )
        }
        return false
      })

      const handleConfirmPartialSeats = () => {
        showPartialSeatModal.value = false
        handleContinue()
      }

      const hasValidSeatSelection = computed(() => {
        if (!journey.value) return false
        if (!seatStore.hasSelectedSeats) return true
        return true
      })

      const hasActiveTransfers = computed(() => {
        if (!journey.value) return false

        const activeOutboundSegments =
          journey.value.outbound_booking_tariff_segments.filter(
            (segment) =>
              !segment.required_products.every((product) => product.cancelled)
          )

        const activeInboundSegments =
          journey.value.inbound_booking_tariff_segments?.filter(
            (segment) =>
              !segment.required_products.every((product) => product.cancelled)
          ) || []

        return (
          activeOutboundSegments.length > 1 || activeInboundSegments.length > 1
        )
      })

      const outboundSelectedSeats = computed(() =>
        seatStore.getSelectedSeatsForLeg('outbound', 0)
      )

      const inboundSelectedSeats = computed(() =>
        seatStore.getSelectedSeatsForLeg('inbound', 0)
      )

      const totalPrice = computed(() => {
        return seatStore.totalSelectedSeatsPrice
      })

      const toggleDrawer = () => {
        isDrawerOpen.value = !isDrawerOpen.value
      }

      const checkMobileView = () => {
        isMobileView.value = window.innerWidth < 674
      }

      onMounted(async () => {
        await loadJourneyData()
        checkMobileView()
        window.addEventListener('resize', checkMobileView)
      })

      onUnmounted(() => {
        window.removeEventListener('resize', checkMobileView)
      })

      const handleBack = () => {
        if (route.query.isRebooking) {
          const queryParams = {
            isRebooking: route.query.isRebooking,
            rebookingType: route.query.rebookingType,
            bookingId: route.query.bookingId,
            itemsToReplace: route.query.itemsToReplace
          }
          router.push({
            name: 'RebookingSearchResultPage',
            query: queryParams
          })
        } else {
          router.push({ name: 'SearchResultPage' })
        }
      }

      return {
        journey,
        isLoading,
        showPartialSeatModal,
        seatStore,
        journeyStore,
        myTravelStore, 
        handleSeatSelected,
        handleContinueClick,
        handleConfirmPartialSeats,
        seatPrices,
        outboundSelectedSeats,
        inboundSelectedSeats,
        handleBack,
        isDrawerOpen,
        toggleDrawer,
        isMobileView,
        chevron,
        handleCarriageLayout,
        handleContinue,
        hasValidSeatSelection,
        hasActiveTransfers,
        totalPrice,
        transferJourneyRef,
        directJourneyRef,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .seat-reservation-page {
    width: 100%;
    margin: 0 auto rem(100);

    @media screen and (min-width: $breakpoint-md) {
      margin-bottom: rem(40);
    }

    &__nav-bar {
      display: flex;
      flex-direction: column;
      width: 100%;
      justify-content: space-between;
      gap: 8px;

      @media screen and (min-width: $breakpoint-sm) {
        flex-direction: row;
        align-items: center;
      }

      &-right {
        display: flex;
        align-items: center;
        flex-direction: column;
        width: 100%;

        @media screen and (min-width: $breakpoint-sm) {
          width: auto;
          flex-direction: row;
        }
      }

      &-middle {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-content: space-between;
        width: 100%;
        margin-left: 0;

        @media screen and (min-width: $breakpoint-sm) {
          flex-direction: column;
          width: auto;
        }
      }

      &-buttons {
        display: flex;
        flex-grow: 1;
        flex-direction: row;
        gap: 16px;
        margin-top: rem(16);

        @media screen and (min-width: $breakpoint-sm) {
          margin-top: 0;

          .button {
            flex: auto;
          }
        }
      }
    }

    .navigation-bar {
      &__lower {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 8px;
      }

      @media screen and (max-width: $breakpoint-sm) {
        &__upper {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 8px;
        }
      }
    }

    &__show-details {
      @include body-large;
      color: $clr-primary;
      background: transparent;
      border: none;
      text-decoration: underline;
      cursor: pointer;

      @media (min-width: 500px) {
        display: none;
      }
    }
  }

  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
</style>